import React, { useContext, useState, useEffect } from 'react'
import { debounce } from 'lodash'
import {
  useToken,
  useBreakpoint,
  DEFAULT_BREAKPOINT,
  useBreakpointValue,
} from '@hub/design-system-base'
import { Reel } from '@hub/reel'
import { Frame } from '@hub/frame'
import { Stack } from '@hub/stack'
import { CoreContainerContext } from '@hub/core-container'
import { LinkBox } from '@hub/link'
import Color from 'color'
import {
  SGPageTemplateContentEntry,
  SGPageTemplateImage,
} from '../../../types/page-templates'
import { CloudinaryImage } from '../cloudinary-image'
import { AsMeasured } from '../as-measured'
import { FeatureControls } from './featureControls'
import { ContentStack } from './contentStack'

const MAX_SLIDES = 5

interface FeatureProps {
  slides: SGPageTemplateContentEntry[]
  analyticsSlot?: string
}
export function pickBestContrast(base: string, options: string[]): string {
  let highestContrast = 0
  let winningOptionIndex = 0
  const baseColor = Color(base)
  for (let i = 0; i < options.length; i++) {
    const optionConstrast = Color(options[i]).contrast(baseColor)
    if (optionConstrast > highestContrast) {
      highestContrast = optionConstrast
      winningOptionIndex = i
    }
  }
  return options[winningOptionIndex]
}
function findSquareImage(
  image: SGPageTemplateImage,
  secondaryImage?: SGPageTemplateImage | null
): SGPageTemplateImage {
  if (image.type === 'SQUARE') {
    return image
  }
  return secondaryImage || image
}

const findTallestSlide = (slides: (HTMLElement | undefined)[]): number =>
  slides.filter(Boolean).reduce((tallestSlideHeight, slide) => {
    const stackElement = slide?.lastElementChild
    const image = stackElement?.firstElementChild?.scrollHeight || 0
    const title =
      stackElement?.lastElementChild?.firstElementChild?.scrollHeight || 0
    const button =
      stackElement?.lastElementChild?.lastElementChild?.scrollHeight || 0
    const stackHeight = image + title + button

    return tallestSlideHeight < stackHeight ? stackHeight : tallestSlideHeight
  }, 0)

// The logic in this component can be hard to grok at first read so here is a
// pseudocode summary of the behaviour:
//
// if wide image present
//   display hero feature mode for desktop
//   display square image for mobile (fall back to only available image even if its wide)
//  else
//    display in square desktop mode with square image

export const Feature: React.FC<FeatureProps> = ({ slides, analyticsSlot }) => {
  const breakpoint = useBreakpoint(DEFAULT_BREAKPOINT) as string
  const container = useContext(CoreContainerContext)
  const wideImageContentLeftOffset = '65%'
  const wideImageContentRightPadding = useToken('space', 'spacing-xl')
  const containerGutter = useToken('space', container.gutter)
  const squareImagePadding = useToken('space', 'spacing-md')
  const defaultWideForegroundColor = useToken('colors', 'white')
  const defaultSquareForegroundColor = useToken('colors', 'licorice')
  const alternateSquareForegroundColor = useToken('colors', 'white')
  const defaultSupportingColor = useToken('colors', 'smoke')

  const isWideMode = useBreakpointValue({
    base: false,
    sm: false,
    md: true,
  })

  const slidesRef = React.useRef<HTMLElement[]>([])
  const [frameHeight, setFrameHeight] = useState<number | undefined>()

  useEffect(() => {
    const DELAY = 150
    const updateSize = debounce(function (): void {
      if (window) {
        setFrameHeight(findTallestSlide(slidesRef.current))
      }
    }, DELAY)

    window.addEventListener('resize', updateSize)
    updateSize()
    return () => {
      window.removeEventListener('resize', updateSize)
    }
  }, [])

  return (
    <Reel
      snap
      includeOverflowInWidthCalculation
      visibleFrames={1}
      gap="spacing-none"
      renderControls={FeatureControls(breakpoint)}
      containerOverflow={[containerGutter, null, 'spacing-none']}
    >
      {slides.slice(0, MAX_SLIDES).map((item, index) => {
        const {
          title,
          image,
          secondaryImage,
          shortDescription,
          callToAction,
        } = item

        const hasWideImage = image.type === 'WIDE'
        const squareImage = findSquareImage(image, secondaryImage)
        const squareSupportingColour =
          squareImage.supportingColour || defaultSupportingColor
        const squareForegroundColor = pickBestContrast(squareSupportingColour, [
          defaultSquareForegroundColor,
          alternateSquareForegroundColor,
        ])
        const contentActionLabel =
          isWideMode === undefined ? '' : callToAction.label
        const contentTitle = isWideMode === undefined ? '' : title

        if (hasWideImage) {
          // We have a wide image so display in Hero Feature mode in desktop and square in mobile
          return (
            <Frame
              ratio={['1', '1', '21/9']}
              key={index}
              sx={{
                height: [
                  `calc(${frameHeight}px + ${squareImagePadding} * 4.25)`,
                  null,
                  'size-full',
                ],
              }}
            >
              <LinkBox
                ref={(el: HTMLElement) => {
                  slidesRef.current[index] = el
                }}
              >
                <Stack
                  as={props => (
                    <AsMeasured
                      {...props}
                      metadata={item.analyticsMetadata}
                      slot={analyticsSlot}
                    />
                  )}
                  direction={['column', 'column', 'row']}
                  gap={['spacing-md', 'spacing-md', 'spacing-none']}
                  shouldWrapChildren={false}
                  sx={{
                    alignItems: [null, null, 'center'],
                    justifyContent: [null, null, 'flex-end'],
                    backgroundColor: squareSupportingColour,
                    paddingX: [containerGutter, containerGutter, 0],
                    paddingY: [squareImagePadding, squareImagePadding, 0],
                    height: 'size-full',
                    width: 'size-full',
                    position: 'relative',
                    isolation: 'isolate',
                  }}
                >
                  {isWideMode ? (
                    <CloudinaryImage
                      ratio={'21/9'}
                      imageSetOrImage={image}
                      sx={{
                        zIndex: 1,
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  ) : (
                    <CloudinaryImage ratio="1" imageSetOrImage={squareImage} />
                  )}
                  <ContentStack
                    centered={!isWideMode}
                    sx={{
                      zIndex: 2,
                      flexGrow: [1, 1, null],
                      justifyContent: [
                        'space-between',
                        'space-between',
                        'flex-end',
                      ],
                      maxWidth: [
                        null,
                        null,
                        `calc(100% - (${wideImageContentLeftOffset} + ${wideImageContentRightPadding}))`,
                      ],
                      marginRight: [
                        null,
                        null,
                        `${wideImageContentRightPadding} !important`,
                      ],
                    }}
                    title={contentTitle}
                    shortDescription={shortDescription ?? undefined}
                    callToActionLabel={contentActionLabel}
                    url={callToAction.url}
                    color={[
                      squareForegroundColor,
                      null,
                      defaultWideForegroundColor,
                    ]}
                    textShadow={[null, null, '0 0 12px rgba(0, 0, 0, 0.7)']}
                  />
                </Stack>
              </LinkBox>
            </Frame>
          )
        } else {
          // TODO: If the only image we have is a wide image, perhaps we can
          // do something with Cloudinary here.
          return (
            <LinkBox
              ref={(el: HTMLElement) => {
                slidesRef.current[index] = el
              }}
              key={index}
            >
              <Stack
                as={props => (
                  <AsMeasured
                    {...props}
                    metadata={item.analyticsMetadata}
                    slot={analyticsSlot}
                  />
                )}
                gap={['spacing-md', 'spacing-md', 'spacing-7']}
                direction={['column', 'column', 'row']}
                shouldWrapChildren={false}
                sx={{
                  alignItems: [null, null, 'center'],
                  backgroundColor: squareSupportingColour,
                  paddingY: squareImagePadding,
                  paddingX: [containerGutter, containerGutter, 0],
                  height: [null, 'size-full', null],
                }}
              >
                <CloudinaryImage
                  ratio="1"
                  sx={{
                    minWidth: [
                      null,
                      null,
                      `calc((100% / (21 / 9)) - (2 * ${squareImagePadding}))`,
                    ],
                    marginLeft: [null, null, squareImagePadding],
                  }}
                  imageSetOrImage={squareImage}
                />
                <ContentStack
                  centered={!isWideMode}
                  title={contentTitle}
                  shortDescription={shortDescription ?? undefined}
                  callToActionLabel={contentActionLabel}
                  url={callToAction.url}
                  color={squareForegroundColor}
                  sx={{
                    paddingRight: [null, null, 'spacing-xl'],
                    flexGrow: [1, 1, null],
                    justifyContent: [null, 'space-between', null],
                  }}
                />
              </Stack>
            </LinkBox>
          )
        }
      })}
    </Reel>
  )
}
